import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { withTimeout } from '@/lib/withTimeout';
import type { Attachment, GroupColor, Knowledge, KnowledgeGroup } from '../types';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import KnowledgeGroupList, {
  type KnowledgeFilter,
} from './knowledge/KnowledgeGroupList';
import KnowledgeGroupEditorModal from './knowledge/KnowledgeGroupEditorModal';
import KnowledgeCard from './knowledge/KnowledgeCard';
import KnowledgeEditorModal, {
  type KnowledgePayload,
} from './knowledge/KnowledgeEditorModal';
import KnowledgeViewModal from './knowledge/KnowledgeViewModal';

export default function KnowledgePanel() {
  const [items, setItems] = useState<Knowledge[]>([]);
  const [groups, setGroups] = useState<KnowledgeGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<KnowledgeFilter>('all');
  const [filterOpen, setFilterOpen] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Knowledge | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [groupEditorOpen, setGroupEditorOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<KnowledgeGroup | null>(null);
  const [groupSaving, setGroupSaving] = useState(false);
  const [groupError, setGroupError] = useState('');

  const [deletingItem, setDeletingItem] = useState<Knowledge | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<KnowledgeGroup | null>(
    null
  );
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [viewing, setViewing] = useState<Knowledge | null>(null);
  const [imageUrls, setImageUrls] = useState<Record<string, string[]>>({});

  // Tải signed URL cho ảnh minh họa (chạy nền, có timeout, không chặn hiển thị danh sách)
  const loadImageUrls = async (itemsData: Knowledge[]) => {
    const urls: Record<string, string[]> = {};
    await Promise.all(
      itemsData
        .filter((i) => i.image_urls?.length)
        .map(async (i) => {
          try {
            const results = await Promise.all(
              i.image_urls.map((p) =>
                withTimeout(
                  supabase.storage
                    .from('noteapp_knowledge_images')
                    .createSignedUrl(p, 3600),
                  8000
                )
              )
            );
            urls[i.id] = results
              .map((r) => r.data?.signedUrl)
              .filter((u): u is string => !!u);
          } catch {
            // Bỏ qua ảnh không tải được
          }
        })
    );
    setImageUrls(urls);
  };

  const fetchAll = useCallback(async (retryCount = 0) => {
    if (retryCount === 0) {
      setLoading(true);
      setError('');
    }
    try {
      const [groupsRes, itemsRes] = await withTimeout(
        Promise.all([
          supabase
            .from('noteapp_knowledge_groups')
            .select('*')
            .order('created_at', { ascending: true }),
          supabase.from('noteapp_knowledge').select('*').order('updated_at', {
            ascending: false,
          }),
        ]),
        30000
      );
      if (groupsRes.error) throw groupsRes.error;
      if (itemsRes.error) throw itemsRes.error;
      setGroups(groupsRes.data ?? []);
      const itemsData = itemsRes.data ?? [];
      setItems(itemsData);
      void loadImageUrls(itemsData);
      setLoading(false);
    } catch (err) {
      console.error('fetchAll error:', err);
      if (retryCount < 2) {
        setTimeout(() => fetchAll(retryCount + 1), 3000);
        return;
      }
      const msg = err instanceof Error ? err.message : String(err);
      setError(`Không thể tải dữ liệu kiến thức. ${msg}`);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const groupMap = new Map(groups.map((g) => [g.id, g]));
  const counts: Record<string, number> = {};
  items.forEach((i) => {
    if (i.group_id) counts[i.group_id] = (counts[i.group_id] ?? 0) + 1;
  });
  const ungroupedCount = items.filter((i) => !i.group_id).length;

  const filtered = items.filter((i) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      i.title.toLowerCase().includes(q) ||
      i.content.toLowerCase().includes(q);
    if (!matchSearch) return false;
    if (activeFilter === 'all') return true;
    if (activeFilter === 'ungrouped') return !i.group_id;
    return i.group_id === activeFilter;
  });

  const openCreate = () => {
    setEditing(null);
    setFormError('');
    setEditorOpen(true);
  };

  const openEdit = (item: Knowledge) => {
    setEditing(item);
    setFormError('');
    setEditorOpen(true);
  };

  const handleSaveItem = async (payload: KnowledgePayload) => {
    if (!payload.title) {
      setFormError('Vui lòng nhập tiêu đề.');
      return;
    }
    setFormError('');
    setSaving(true);
    try {
      const data: {
        title: string;
        content: string;
        category: string;
        group_id: string | null;
        image_urls: string[];
        attachments: Attachment[];
      } = {
        title: payload.title,
        content: payload.content,
        category: '',
        group_id: payload.groupId,
        image_urls: payload.existingImageUrls,
        attachments: payload.existingAttachments,
      };

      // Tải các ảnh minh họa mới lên
      const uploadedPaths: string[] = [];
      if (payload.imageFiles.length) {
        const { data: authData } = await supabase.auth.getUser();
        const userId = authData.user?.id ?? 'unknown';
        for (const file of payload.imageFiles) {
          const rawExt = (file.name.split('.').pop() ?? 'png')
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '');
          const safeExt = rawExt || 'png';
          const path = `${userId}/${Date.now()}-${crypto.randomUUID()}.${safeExt}`;
          const { error: uploadErr } = await supabase.storage
            .from('noteapp_knowledge_images')
            .upload(path, file, { cacheControl: '3600', upsert: false });
          if (uploadErr) throw uploadErr;
          uploadedPaths.push(path);
        }
      }
      data.image_urls = [...payload.existingImageUrls, ...uploadedPaths];

      // Tải các file đính kèm mới lên
      const uploadedAttachments: Attachment[] = [];
      if (payload.attachmentFiles.length) {
        const { data: authData } = await supabase.auth.getUser();
        const userId = authData.user?.id ?? 'unknown';
        for (const file of payload.attachmentFiles) {
          const rawExt = (file.name.split('.').pop() ?? '')
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '');
          const safeExt = rawExt ? `.${rawExt}` : '';
          const path = `${userId}/files/${Date.now()}-${crypto.randomUUID()}${safeExt}`;
          const { error: uploadErr } = await supabase.storage
            .from('noteapp_attachments')
            .upload(path, file, { cacheControl: '3600', upsert: false });
          if (uploadErr) throw uploadErr;
          uploadedAttachments.push({
            path,
            name: file.name,
            size: file.size,
            type: file.type,
          });
        }
      }
      data.attachments = [...payload.existingAttachments, ...uploadedAttachments];

      // Xóa ảnh cũ bị gỡ bỏ khỏi storage
      if (editing?.image_urls?.length) {
        const removed = editing.image_urls.filter(
          (p) => !payload.existingImageUrls.includes(p)
        );
        if (removed.length) {
          await supabase.storage.from('noteapp_knowledge_images').remove(removed);
        }
      }

      // Xóa file đính kèm cũ bị gỡ bỏ khỏi storage
      if (editing?.attachments?.length) {
        const removedPaths = editing.attachments
          .filter(
            (a) =>
              !payload.existingAttachments.some((k) => k.path === a.path)
          )
          .map((a) => a.path);
        if (removedPaths.length) {
          await supabase.storage
            .from('noteapp_attachments')
            .remove(removedPaths);
        }
      }

      if (editing) {
        const { error: err } = await supabase
          .from('noteapp_knowledge')
          .update(data)
          .eq('id', editing.id);
        if (err) throw err;
      } else {
        const { error: err } = await supabase.from('noteapp_knowledge').insert(data);
        if (err) throw err;
      }
      setEditorOpen(false);
      await fetchAll();
    } catch {
      setFormError('Không thể lưu kiến thức. Vui lòng thử lại.');
    } finally {
      setSaving(false);
    }
  };

  const openAddGroup = () => {
    setEditingGroup(null);
    setGroupError('');
    setGroupEditorOpen(true);
  };

  const openEditGroup = (group: KnowledgeGroup) => {
    setEditingGroup(group);
    setGroupError('');
    setGroupEditorOpen(true);
  };

  const handleSaveGroup = async (name: string, color: GroupColor) => {
    setGroupError('');
    setGroupSaving(true);
    try {
      if (editingGroup) {
        const { error: err } = await supabase
          .from('noteapp_knowledge_groups')
          .update({ name, color })
          .eq('id', editingGroup.id);
        if (err) throw err;
      } else {
        const { error: err } = await supabase
          .from('noteapp_knowledge_groups')
          .insert({ name, color });
        if (err) throw err;
      }
      setGroupEditorOpen(false);
      await fetchAll();
    } catch {
      setGroupError('Không thể lưu phân loại. Vui lòng thử lại.');
    } finally {
      setGroupSaving(false);
    }
  };

  const handleDeleteItem = async () => {
    if (!deletingItem) return;
    setDeleteLoading(true);
    try {
      const { error: err } = await supabase
        .from('noteapp_knowledge')
        .delete()
        .eq('id', deletingItem.id);
      if (err) throw err;

      // Xóa sạch ảnh minh họa và file đính kèm khỏi server
      // (best-effort, không chặn việc xóa kiến thức nếu xóa file gặp lỗi)
      const storageTasks: Promise<unknown>[] = [];
      if (deletingItem.image_urls?.length) {
        storageTasks.push(
          supabase.storage
            .from('noteapp_knowledge_images')
            .remove(deletingItem.image_urls)
        );
      }
      if (deletingItem.attachments?.length) {
        storageTasks.push(
          supabase.storage
            .from('noteapp_attachments')
            .remove(deletingItem.attachments.map((a) => a.path))
        );
      }
      await Promise.allSettled(storageTasks);

      setDeletingItem(null);
      await fetchAll();
    } catch {
      setError('Không thể xóa kiến thức. Vui lòng thử lại.');
      setDeletingItem(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!deletingGroup) return;
    setDeleteLoading(true);
    try {
      const { error: err } = await supabase
        .from('noteapp_knowledge_groups')
        .delete()
        .eq('id', deletingGroup.id);
      if (err) throw err;
      if (activeFilter === deletingGroup.id) setActiveFilter('all');
      setDeletingGroup(null);
      await fetchAll();
    } catch {
      setDeletingGroup(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const defaultGroupId =
    activeFilter !== 'all' && activeFilter !== 'ungrouped'
      ? activeFilter
      : null;

  const activeFilterLabel =
    activeFilter === 'all'
      ? 'Tất cả'
      : activeFilter === 'ungrouped'
        ? 'Chưa phân loại'
        : (groups.find((g) => g.id === activeFilter)?.name ?? 'Tất cả');

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex flex-col gap-3 border-b border-background-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5">
        <div>
          <h2 className="font-heading text-lg font-semibold text-foreground-950 sm:text-xl">
            Kiến thức
          </h2>
          <p className="text-sm text-foreground-500">
            Lưu trữ kiến thức và tài liệu tham khảo của bạn.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm kiếm..."
              className="w-full rounded-lg border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-base text-foreground-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-200 sm:w-56 sm:py-2 sm:text-sm"
            />
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-3 text-base font-semibold text-background-50 transition hover:bg-primary-600 sm:w-auto sm:py-2 sm:text-sm"
          >
            <i className="ri-add-line text-lg" />
            Thêm kiến thức
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Desktop group list */}
        <div className="hidden md:block">
          <KnowledgeGroupList
            groups={groups}
            counts={counts}
            ungroupedCount={ungroupedCount}
            activeFilter={activeFilter}
            onSelect={setActiveFilter}
            onAddGroup={openAddGroup}
            onEditGroup={openEditGroup}
            onDeleteGroup={setDeletingGroup}
          />
        </div>

        {/* Main content */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Mobile group filter dropdown */}
          <div className="relative flex items-center gap-2 border-b border-background-200 px-4 py-3 md:hidden">
            <button
              type="button"
              onClick={() => setFilterOpen((v) => !v)}
              className="flex flex-1 items-center justify-between gap-2 whitespace-nowrap rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm font-medium text-foreground-900 transition hover:border-primary-400"
            >
              <span className="truncate">{activeFilterLabel}</span>
              <i
                className={`ri-arrow-down-s-line text-base text-foreground-500 transition-transform ${
                  filterOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            <button
              type="button"
              onClick={openAddGroup}
              className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-lg border-2 border-dashed border-orange-400 px-3 py-2.5 text-sm font-medium text-orange-600 transition hover:border-orange-500 hover:bg-orange-50 hover:text-orange-700"
            >
              <i className="ri-add-line" />
              Phân loại
            </button>

            {filterOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setFilterOpen(false)}
                />
                <div className="absolute left-4 right-4 top-full z-20 mt-1 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                  <button
                    type="button"
                    onClick={() => {
                      setActiveFilter('all');
                      setFilterOpen(false);
                    }}
                    className={`flex w-full items-center justify-between whitespace-nowrap px-4 py-3 text-sm font-medium transition ${
                      activeFilter === 'all'
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    Tất cả
                    {activeFilter === 'all' && (
                      <i className="ri-check-line text-base" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveFilter('ungrouped');
                      setFilterOpen(false);
                    }}
                    className={`flex w-full items-center justify-between whitespace-nowrap px-4 py-3 text-sm font-medium transition ${
                      activeFilter === 'ungrouped'
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    Chưa phân loại
                    {activeFilter === 'ungrouped' && (
                      <i className="ri-check-line text-base" />
                    )}
                  </button>
                  {groups.map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => {
                        setActiveFilter(g.id);
                        setFilterOpen(false);
                      }}
                      className={`flex w-full items-center justify-between whitespace-nowrap px-4 py-3 text-sm font-medium transition ${
                        activeFilter === g.id
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      {g.name}
                      {activeFilter === g.id && (
                        <i className="ri-check-line text-base" />
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Knowledge list */}
          <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="h-7 w-7 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center gap-3 py-20">
                <i className="ri-error-warning-line text-3xl text-accent-500" />
                <p className="text-sm text-foreground-500">{error}</p>
                <button
                  type="button"
                  onClick={fetchAll}
                  className="whitespace-nowrap rounded-lg border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 transition hover:bg-background-100"
                >
                  Thử lại
                </button>
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-lightbulb-line text-2xl" />
                </div>
                <p className="text-sm text-foreground-500">
                  {items.length === 0
                    ? 'Chưa có kiến thức nào. Hãy thêm kiến thức đầu tiên.'
                    : 'Không tìm thấy kiến thức phù hợp.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 xl:grid-cols-3">
                {filtered.map((item) => (
                  <KnowledgeCard
                    key={item.id}
                    item={item}
                    group={
                      item.group_id ? groupMap.get(item.group_id) : undefined
                    }
                    onView={setViewing}
                    onEdit={openEdit}
                    onDelete={setDeletingItem}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Knowledge editor */}
      <KnowledgeEditorModal
        open={editorOpen}
        editing={editing}
        groups={groups}
        defaultGroupId={defaultGroupId}
        saving={saving}
        error={formError}
        onClose={() => setEditorOpen(false)}
        onSave={handleSaveItem}
      />

      {/* Knowledge view detail */}
      <KnowledgeViewModal
        item={viewing}
        group={viewing?.group_id ? groupMap.get(viewing.group_id) : undefined}
        imageUrls={viewing ? imageUrls[viewing.id] : undefined}
        onClose={() => setViewing(null)}
        onEdit={(item) => {
          setViewing(null);
          openEdit(item);
        }}
      />

      {/* Group editor */}
      <KnowledgeGroupEditorModal
        open={groupEditorOpen}
        editing={editingGroup}
        saving={groupSaving}
        error={groupError}
        onClose={() => setGroupEditorOpen(false)}
        onSave={handleSaveGroup}
      />

      {/* Delete item confirm */}
      <ConfirmDialog
        open={!!deletingItem}
        title="Xóa kiến thức"
        message={`Bạn có chắc muốn xóa kiến thức "${deletingItem?.title}"? Hành động này không thể hoàn tác.`}
        loading={deleteLoading}
        onCancel={() => setDeletingItem(null)}
        onConfirm={handleDeleteItem}
      />

      {/* Delete group confirm */}
      <ConfirmDialog
        open={!!deletingGroup}
        title="Xóa phân loại"
        message={`Bạn có chắc muốn xóa phân loại "${deletingGroup?.name}"? Các kiến thức trong phân loại sẽ chuyển về "Chưa phân loại".`}
        loading={deleteLoading}
        onCancel={() => setDeletingGroup(null)}
        onConfirm={handleDeleteGroup}
      />
    </div>
  );
}